import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Subscription, fromEvent, timer, EMPTY, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { IdleService } from './idle.service';
import { SocketService } from './socket.service';

const IDLE_PAUSE_MS = 60_000;
const PRESENCE_INACTIVE_MS = 120_000;

@Injectable({ providedIn: 'root' })
export class SessionActivityService implements OnDestroy {
  private paused = false;
  private started = false;
  private connectedSub?: Subscription;
  private authenticatedSub?: Subscription;
  private visibilitySub?: Subscription;
  private idleTimerSub?: Subscription;
  private resumeOnActivitySub?: Subscription;
  private presenceActivitySub?: Subscription;
  private presenceTimerSub?: Subscription;
  private presenceInactive = false;

  constructor(
    private idleService: IdleService,
    private socketService: SocketService,
    private zone: NgZone,
  ) {}

  start(): void {
    if (this.started) return;
    this.started = true;

    this.connectedSub = this.socketService.connected.subscribe(connected => {
      if (connected) {
        this.bind();
      } else {
        this.unbind();
        this.paused = false;
      }
    });
    this.authenticatedSub = this.socketService.authenticated.subscribe(authenticated => {
      if (authenticated) {
        this.syncAuthenticatedState();
      }
    });
  }

  ngOnDestroy(): void {
    this.connectedSub?.unsubscribe();
    this.authenticatedSub?.unsubscribe();
    this.unbind();
  }

  private bind(): void {
    this.zone.runOutsideAngular(() => {
      this.visibilitySub = fromEvent(document, 'visibilitychange').subscribe(() => {
        if (document.visibilityState === 'hidden') {
          this.pause(this.nowIso());
        } else if (document.visibilityState === 'visible') {
          this.resume(this.nowIso());
        }
      });

      this.idleTimerSub = this.idleService.activity$
        .pipe(switchMap(() => this.paused ? EMPTY as Observable<number> : timer(IDLE_PAUSE_MS)))
        .subscribe(() => {
          const lastActivity = this.idleService.lastActivityAt || Date.now();
          this.pause(this.toIso(new Date(lastActivity)));

          this.resumeOnActivitySub?.unsubscribe();
          this.resumeOnActivitySub = this.idleService.activity$.subscribe(() => this.resume(this.nowIso()));
        });

      if (document.visibilityState === 'hidden') {
        this.pause(this.nowIso());
      }

      this.presenceActivitySub = this.idleService.activity$.subscribe(() => {
        if (this.presenceInactive) {
          this.presenceInactive = false;
          this.socketService.emitUserPresenceChanged(false);
        }
        this.schedulePresenceInactive();
      });
      this.schedulePresenceInactive();
    });
  }

  private unbind(): void {
    this.visibilitySub?.unsubscribe();
    this.idleTimerSub?.unsubscribe();
    this.resumeOnActivitySub?.unsubscribe();
    this.presenceActivitySub?.unsubscribe();
    this.presenceTimerSub?.unsubscribe();
    this.visibilitySub = undefined;
    this.idleTimerSub = undefined;
    this.resumeOnActivitySub = undefined;
    this.presenceActivitySub = undefined;
    this.presenceTimerSub = undefined;
    this.presenceInactive = false;
  }

  private schedulePresenceInactive(): void {
    this.presenceTimerSub?.unsubscribe();
    this.presenceTimerSub = timer(PRESENCE_INACTIVE_MS).subscribe(() => {
      this.presenceInactive = true;
      this.socketService.emitUserPresenceChanged(true);
    });
  }

  private syncAuthenticatedState(): void {
    this.socketService.emitUserPresenceChanged(this.presenceInactive);
    if (this.paused) {
      this.socketService.emitSessionPause(this.nowIso());
    }
  }

  private pause(timestamp: string): void {
    if (this.paused) return;
    this.paused = true;
    this.socketService.emitSessionPause(timestamp);
  }

  private resume(timestamp: string): void {
    if (!this.paused) return;
    this.paused = false;
    this.resumeOnActivitySub?.unsubscribe();
    this.resumeOnActivitySub = undefined;
    this.socketService.emitSessionResume(timestamp);
  }

  private nowIso(): string {
    return this.toIso(new Date());
  }

  private toIso(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} `
      + `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;
  }
}
